import { initVBO } from './initVBO';
import BASE_VERTEX_SHADER from './shaders/BaseVertexShader.glsl?raw';
import INTERPOLATION_FRAGMENT_SHADER from './shaders/InterpolationFragmentShader.glsl?raw';
import SCREEN_FRAGMENT_SHADER from './shaders/ScreenFragmentShader.glsl?raw';

export const initWebGL = (
  canvas: HTMLCanvasElement,
  video: HTMLVideoElement | null,
  poster: HTMLImageElement
): ((loaded: boolean) => void) | undefined => {
  let gl: WebGLRenderingContext | null;

  if (!window.WebGLRenderingContext) {
    return;
  }

  try {
    gl = canvas.getContext('webgl');
    if (!gl) return;
  } catch (e) {
    return;
  }

  //Configuration
  gl.enable(gl.CULL_FACE);
  gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
  initVBO(gl);

  let reverseOrder: boolean = false;

  enum TextureSlots {
    Video = gl.TEXTURE0,
    Previous = gl.TEXTURE1,
    Current = gl.TEXTURE2,
  }

  const initTexture = (gl: WebGLRenderingContext): WebGLTexture | null => {
    const texture = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, texture);

    const level = 0;
    const internalFormat = gl.RGBA;
    const width = 160;
    const height = 90;
    const border = 0;
    const srcFormat = gl.RGBA;
    const srcType = gl.UNSIGNED_BYTE;
    const data = new Uint8Array(width * height * 4).fill(0);
    gl.texImage2D(gl.TEXTURE_2D, level, internalFormat, width, height, border, srcFormat, srcType, data);

    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);

    return texture;
  };

  const videoTexture = initTexture(gl);
  const previousTexture = initTexture(gl);
  const currentTexture = initTexture(gl);
  if (!videoTexture || !previousTexture || !currentTexture) {
    return;
  }

  // Create our vertex shader
  const VS = gl.createShader(gl.VERTEX_SHADER);
  if (!VS) return;
  gl.shaderSource(VS, BASE_VERTEX_SHADER);
  gl.compileShader(VS);

  const screenFS = gl.createShader(gl.FRAGMENT_SHADER);
  if (!screenFS) return;
  gl.shaderSource(screenFS, SCREEN_FRAGMENT_SHADER);
  gl.compileShader(screenFS);

  const interpolationFS = gl.createShader(gl.FRAGMENT_SHADER);
  if (!interpolationFS) return;
  gl.shaderSource(interpolationFS, INTERPOLATION_FRAGMENT_SHADER);
  gl.compileShader(interpolationFS);

  const screenProgram = gl.createProgram();
  if (!screenProgram) return;
  gl.attachShader(screenProgram, VS);
  gl.attachShader(screenProgram, screenFS);
  gl.linkProgram(screenProgram);

  const interpolationProgram = gl.createProgram();
  if (!interpolationProgram) return;
  gl.attachShader(interpolationProgram, VS);
  gl.attachShader(interpolationProgram, interpolationFS);
  gl.linkProgram(interpolationProgram);

  const screenPositionLocation = gl.getAttribLocation(screenProgram, 'position');
  gl.vertexAttribPointer(screenPositionLocation, 2, gl.FLOAT, false, 8, 0);
  gl.enableVertexAttribArray(screenPositionLocation);

  const basePositionLocation = gl.getAttribLocation(interpolationProgram, 'position');
  gl.vertexAttribPointer(basePositionLocation, 2, gl.FLOAT, false, 8, 0);
  gl.enableVertexAttribArray(basePositionLocation);

  gl.useProgram(screenProgram);
  gl.uniform1i(gl.getUniformLocation(screenProgram, 'screen'), 1);
  gl.useProgram(interpolationProgram);
  gl.uniform1i(gl.getUniformLocation(interpolationProgram, 'video'), 0);
  gl.uniform1i(gl.getUniformLocation(interpolationProgram, 'previous'), 2);

  const fbo = gl.createFramebuffer();
  if (!fbo) return;
  gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);

  gl.activeTexture(TextureSlots.Video);
  gl.bindTexture(gl.TEXTURE_2D, videoTexture);

  const level = 0;
  const internalFormat = gl.RGBA;
  const srcFormat = gl.RGBA;
  const srcType = gl.UNSIGNED_BYTE;

  return (loaded: boolean): void => {
    if (!gl) return;

    const image = loaded && video && !video.paused
      ? video
      : poster;

    gl.activeTexture(TextureSlots.Video);
    gl.texImage2D(gl.TEXTURE_2D, level, internalFormat, srcFormat, srcType, image);

    //interpolate
    gl.useProgram(interpolationProgram);
    gl.viewport(0, 0, 160, 90);
    gl.activeTexture(TextureSlots.Previous);
    gl.bindTexture(gl.TEXTURE_2D, reverseOrder ? currentTexture : previousTexture);
    gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
    gl.framebufferTexture2D(
      gl.FRAMEBUFFER,
      gl.COLOR_ATTACHMENT0,
      gl.TEXTURE_2D,
      reverseOrder ? previousTexture : currentTexture,
      0
    );
    gl.drawArrays(gl.TRIANGLES, 0, 3);

    //render
    const width = poster.offsetWidth / 15;
    const height = poster.offsetHeight / 15;
    gl.useProgram(screenProgram);
    gl.viewport((canvas.width - width) / 2, canvas.height - height, width, height);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.activeTexture(TextureSlots.Current);
    gl.bindTexture(gl.TEXTURE_2D, reverseOrder ? previousTexture : currentTexture);
    gl.drawArrays(gl.TRIANGLES, 0, 3);

    reverseOrder = !reverseOrder;
  };
};
